import React, { FunctionComponent, memo } from 'react';

import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import AppState from '@webapp/store/state';

import { scaleLinear } from 'd3-scale';

import { ComposableMap, Geographies, Geography } from 'react-simple-maps';

import MapData from './map-data.json';

import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';

import { numberFormatter } from './utils';
import * as countries from '@codeforcer/i18n-iso-countries-extended';
import lang from '@codeforcer/i18n-iso-countries-extended/langs/en.json';

countries.registerLocale(lang);

interface LocationsMapProps {
  className?: string;
  listeners: any;
  streams: any;
  isMobile: boolean;
  setTooltipContent: (value: React.ReactNode) => void;
}

const mapStateToProps = (state: AppState) => ({
  listeners: state.artistAnalytics.data?.listenersOnTerritory,
  streams: state.artistAnalytics.data?.topLocations,
  isMobile: state.ui.mobile,
});

interface LocationsTooltipProps {
  label: string;
  streams?: number;
  listeners?: number;
}

const TooltipContent = ({
  label,
  streams,
  listeners,
}: LocationsTooltipProps) => {
  return (
    <Grid gap={10}>
      <Typography weight="700">{label}</Typography>
      {streams && (
        <Flex alignItems="baseline" gap={4}>
          <Typography weight="700" capitalize>
            <Translate id="ANALYTICS.STREAMS" />
          </Typography>
          <Typography>{numberFormatter.format(Number(streams))}</Typography>
        </Flex>
      )}
      {listeners && (
        <Flex alignItems="baseline" gap={4}>
          <Typography weight="700" capitalize>
            <Translate id="ANALYTICS.LISTENERS" />
          </Typography>
          <Typography>{numberFormatter.format(Number(listeners))}</Typography>
        </Flex>
      )}
    </Grid>
  );
};

const LocationsMap: FunctionComponent<LocationsMapProps> = ({
  className,
  listeners,
  streams,
  isMobile,
  setTooltipContent,
}) => {
  const theme = useTheme();

  const colorScale = scaleLinear<string>()
    .domain([0, 1000000])
    .range([rgba(theme.colors.blueColor, 0.1), theme.colors.blueColor]);

  const getBaseColor = () => {
    return theme.colors.tertiary;
  };

  return (
    <div className={className || ''} data-tip="">
      <ComposableMap
        projection="geoMercator"
        projectionConfig={{
          center: [0, isMobile ? 50 : 20],
          scale: 125,
        }}
      >
        <Geographies geography={MapData}>
          {({ geographies }) =>
            geographies.map((geo) => {
              const stream = streams.find(
                (s) =>
                  countries.getName(s.category, 'en') === geo.properties.name
              );
              const listener = listeners.find(
                (l) =>
                  countries.getName(l.category, 'en') === geo.properties.name
              );
              return (
                <Geography
                  key={geo.rsmKey}
                  geography={geo}
                  fill={stream ? colorScale(stream.value) : getBaseColor()}
                  onMouseEnter={() => {
                    setTooltipContent(
                      <TooltipContent
                        label={geo.properties.name}
                        streams={stream?.value}
                        listeners={listener?.value}
                      />
                    );
                  }}
                  onMouseLeave={() => {
                    setTooltipContent('');
                  }}
                  onTouchStart={() => {
                    setTooltipContent(
                      <TooltipContent
                        label={geo.properties.name}
                        streams={stream?.value}
                        listeners={listener?.value}
                      />
                    );
                  }}
                  onTouchEnd={() => {
                    setTooltipContent('');
                  }}
                />
              );
            })
          }
        </Geographies>
      </ComposableMap>
    </div>
  );
};

export default connect(mapStateToProps)(
  memo(styled(LocationsMap)`
    width: 100%;
    max-height: 390px;

    * > path {
      outline: none !important;
    }
  `)
);
